import { useTranslation } from "react-i18next";
import { CircularProgress, Modal, Snackbar } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { theme } from "../../Register/AddRegister/FormTheme";
import CustomAutocomplete from "../../Global/CustomAutocomplete";
import { useEffect, useState } from "react";
import useAxios from "../../../hook/useAxios";
import { useForm } from "react-hook-form";
import TranslationField from "../../../type/translationField";

import file from "../../../type/file";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import DragDropZipFile from "../../Global/DragDropZipFile";
import PlantingList from "../../../type/plantingList";

type FormValues = {
  planting: {
    name_transes: TranslationField;
    slug: string;
    all_dates: string;
    date: string;
    is_register_completed: boolean;
    community_initials: string;
    community_slug: string;
  };
  fileAddress: string;
};

const ImportImage = ({
  handleModalClose,
  onImageImportSuccess,
  plantingId,
}: {
  handleModalClose: () => void;
  onImageImportSuccess: (slug: string) => void;
  plantingId: any;
}) => {
  const { t, i18n } = useTranslation();
  const [eventData, setEventData] = useState<PlantingList[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<file[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [mandatoryError, isMandatoryError] = useState<boolean>(false);
  const [emptyPlanting, isEmptyPlanting] = useState<boolean>(false);
  const [localSlug, setLocalSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { control, watch, setValue } = useForm<FormValues>();
  const selectedPlanting = watch("planting");
  const slug = selectedPlanting?.slug || "";

  const { fetchData, response } = useAxios(
    process.env.REACT_APP_API_URL +
      `/api/admin/registers/${slug}/import_image/`,
    "POST",
    false,
    `${t(
      "Message.Import process initialization message"
    )}`,
    true,
    true
  );

  useEffect(() => {
    if (response) {
      setLoading(false);
      handleModalClose();
      setSnackbarOpen(true);
      onImageImportSuccess(localSlug!);
    }
  }, [response, handleModalClose, localSlug, onImageImportSuccess]);

  useEffect(() => {
    if (selectedPlanting && selectedPlanting.slug) {
      setLocalSlug(selectedPlanting.slug);
      isEmptyPlanting(false);
    }
  }, [selectedPlanting]);

  const { response: plantingAllRes } = useAxios(
    process.env.REACT_APP_API_URL + "/api/admin/plantings/all/",
    "GET",
    true,
    "",
    true,
    false
  );
  useEffect(() => {
    if (plantingAllRes) {
      setEventData([...plantingAllRes.data]);
      if (plantingId) {
        const selectedPlanting = plantingAllRes.data.find(
          (item: any) => item.slug === plantingId
        );
        setValue("planting", selectedPlanting);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantingAllRes, plantingId]);

  const insertFile = (newFiles: file[]) => {
    setLoading(false);
    if (!newFiles[0].type.includes("pplication/zip")) {
      toast.error(`${t("Message.The supported formats is ZIP")}`);
      return;
    }
    if (newFiles[0].size > 52428800) {
      // 50 MB size check
      toast.error(`${t("Message.Maximum file size is 50 MB")}`);
    } else {
      setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
      isMandatoryError(false);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onImport = () => {
    isEmptyPlanting(false);
    isMandatoryError(false);

    let hasError = false;

    if (!localSlug) {
      isEmptyPlanting(true);
      hasError = true;
    }

    if (selectedFiles.length === 0) {
      isMandatoryError(true);
      hasError = true;
    }
    if (hasError) {
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("slug", localSlug!!);
    formData.append("file_address", selectedFiles[0].data);
    setLoading(true);
    fetchData(formData);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getLabel = (option: any) => {
    const { en, fr } = option.name_transes || {};
    const { date } = option;

    if (i18n.language === "fr") {
      if (fr === "") {
        return date ? `${en} - ${date}` : en;
      } else {
        return date ? `${fr} - ${date}` : fr;
      }
    } else {
      return date ? `${en} - ${date}` : en;
    }
  };

  return (
    <Modal
      open
      onClose={() => !loading && handleModalClose()}
      className="w-full h-full z-[9990] bg-ph-gray bg-opacity-70 flex items-center justify-center fixed top-0 left-0 overflow-scroll box-border"
    >
      <div className="py-12 px-8 bg-ph-white w-[90%] max-h-[90vh] md:w-[65%] lg:w-[40%] overflow-auto">
        <div className="font-semibold text-default-text text-base">
          {t("Import register.Import images")}
        </div>

        <ThemeProvider theme={theme}>
          <div className="flex flex-wrap justify-between w-full mt-8">
            <div className="w-full">
              <p className="text-sm mb-[6px]">
                {t("Register.Planting event")} *
              </p>

              <CustomAutocomplete
                rules={{
                  required: `${t("Message.Required field")}`,
                }}
                control={control}
                name="planting"
                selectOptions={eventData}
                placeholder={t("Register.Planting event")}
                getOptionLabel={getLabel}
              />
              {emptyPlanting && (
                <p className="error-text">{t("Message.Required field")}</p>
              )}
            </div>
          </div>
          <div>
            <div className="flex flex-row items-center">
              <p className="text-sm text-default-text mt-7 mb-[6px]">
                {t("Import register.Images Zip file")}
              </p>
              <FontAwesomeIcon
                className="w-[14px] h-[14px] ml-2 mt-5 text-ph-gray-text"
                icon={faCircleInfo}
                title={`${t("Import register.Zip file info")}`}
              />
            </div>
            <DragDropZipFile
              format="Zip (max 50 MB)"
              removeFile={removeFile}
              insertFile={insertFile}
              files={selectedFiles}
              mandatoryError={mandatoryError}
            />
          </div>

          <div className="mt-5 flex flex-row justify-end">
            <button
              onClick={() => {
                !loading && handleModalClose();
              }}
              type="button"
              className="text-default-text text-sm mt-16rounded-3xl px-6 py-2 mt-5 font-medium"
              disabled={loading}
            >
              {t("Message.Cancel")}
            </button>
            <button
              onClick={onImport}
              type="button"
              className="main-btn text-sm px-6 py-2 mt-5 float-right font-medium"
              disabled={loading}
            >
              {loading && (
                <CircularProgress size={18} color="inherit" className="mr-2" />
              )}
              {t("Import")}
            </button>
          </div>
        </ThemeProvider>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={t("Message.Import process initialization message")}
        />
      </div>
    </Modal>
  );
};

export default ImportImage;
